<!-- src/views/admin/ClientDetails.vue -->
<template>
  <admin-layout :title="`Client: ${client ? client.name : 'Chargement...'}`">
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>

    <div v-else-if="!client" class="text-center py-12 text-gray-500">
      Client introuvable.
    </div>

    <div v-else>
      <!-- Suppression demandée par le titulaire (B9) -->
      <div v-if="client.suppression" class="mb-6 rounded-lg border border-red-300 bg-red-50 p-4">
        <h3 class="text-base font-semibold text-red-800">Suppression du compte demandée par le client</h3>
        <p class="mt-1 text-sm text-red-800">
          Demandée le {{ dateParis(client.suppression.demandee_le) }}. Le compte est suspendu : aucune alerte ne part
          (SMS, appels, notifications) et le client ne peut plus que l'annuler. Il sera <strong>effacé le
          {{ dateParis(client.suppression.effacement_le) }}</strong> : compte, membres, historique, et ses cartes détachées.
        </p>
        <p class="mt-1 text-xs text-red-700">N'annulez qu'à la demande du client (par téléphone, s'il ne peut plus se connecter) : il sera prévenu par courriel.</p>
        <button @click="annulerSuppression" :disabled="actionLoading" class="btn btn-primary mt-3 text-sm">Annuler la suppression</button>
      </div>

      <!-- Infos client -->
      <div class="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div class="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 class="text-lg leading-6 font-medium text-gray-900">Informations client</h3>
          </div>
          <span :class="`px-2 py-1 text-xs font-semibold rounded-full ${client.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`">
            {{ client.active ? 'Actif' : 'Inactif' }}
          </span>
        </div>

        <div class="border-t border-gray-200">
          <dl>
            <div class="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Nom</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span v-if="!editMode">{{ client.name }}</span>
                <input v-else type="text" v-model="editedClient.name" class="form-control" />
              </dd>
            </div>
            <div class="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Contact</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span v-if="!editMode">{{ client.contact_name }}</span>
                <input v-else type="text" v-model="editedClient.contact_name" class="form-control" />
              </dd>
            </div>
            <div class="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Email</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span v-if="!editMode">{{ client.email }}</span>
                <input v-else type="email" v-model="editedClient.email" class="form-control" />
              </dd>
            </div>
            <div class="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Créé le</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ formatDate(client.created_at) }}</dd>
            </div>
            <div class="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Actions</dt>
              <dd class="mt-1 sm:mt-0 sm:col-span-2">
                <div class="flex flex-wrap gap-2">
                  <template v-if="!editMode">
                    <button @click="startEdit" class="btn btn-primary text-sm">Modifier</button>
                  </template>
                  <template v-else>
                    <button @click="saveChanges" class="btn btn-success text-sm" :disabled="actionLoading">Enregistrer</button>
                    <button @click="cancelEdit" class="btn btn-secondary text-sm">Annuler</button>
                  </template>
                  <button v-if="client.active" @click="toggleClientStatus(false)" class="btn btn-warning text-sm" :disabled="actionLoading">Désactiver</button>
                  <button v-else @click="toggleClientStatus(true)" class="btn btn-success text-sm" :disabled="actionLoading">Activer</button>
                  <button @click="deleteClient" class="btn btn-danger text-sm" :disabled="actionLoading">Supprimer</button>
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Dispositifs -->
      <div class="mb-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold text-gray-800">Dispositifs UltraLINK</h2>
          <button @click="showAddDeviceModal = true" class="btn btn-primary">Ajouter un dispositif</button>
        </div>

        <div v-if="devices.length === 0" class="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Aucun dispositif. Cliquez sur "Ajouter un dispositif" pour en créer un.
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="device in devices" :key="device.id"
               class="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
               @click="$router.push(`/admin/devices/${device.id}`)">
            <div class="flex justify-between items-start mb-2">
              <div>
                <h3 class="font-semibold text-gray-900">{{ device.name }}</h3>
                <p class="text-sm text-gray-500">{{ device.location }}</p>
              </div>
              <span :class="`px-2 py-1 text-xs font-semibold rounded-full ${getStateBadge(device)}`">
                {{ getStateLabel(device) }}
              </span>
            </div>
            <div class="text-xs text-gray-400 font-mono">{{ device.ident }}</div>
            <div v-if="device.last_seen" class="mt-2 text-xs text-gray-400">
              Vu le {{ formatDate(device.last_seen) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Comptes partagés : les personnes que le titulaire a invitées sur son compte. -->
      <div class="mb-8">
        <h2 class="text-xl font-semibold text-gray-800 mb-4">Membres du compte</h2>
        <div v-if="avisMembres" class="mb-3 rounded-md border p-3 text-sm"
             :class="avisMembres.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'">{{ avisMembres.texte }}</div>
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div v-if="!membres.length" class="p-4 text-sm text-gray-500">
            Aucun membre : seul le titulaire accède à ce compte.
            <span v-if="invitationsMembres.length"> {{ invitationsMembres.length }} invitation(s) en attente.</span>
          </div>
          <ul v-else class="divide-y divide-gray-100">
            <li v-for="m in membres" :key="m.id" class="px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
              <div>
                <span class="font-medium text-gray-900">{{ m.name }}</span>
                <span class="ml-2 text-gray-500">{{ m.email }}</span>
                <span class="block text-xs text-gray-500">
                  {{ m.role === 'observateur' ? 'Observateur' : 'Utilisateur' }}
                  · {{ m.all_devices ? 'tous les appareils' : `${(m.device_ids || []).length} appareil(s)` }}
                  · 2FA {{ m.mfa && m.mfa.actif ? 'active' : 'absente' }}
                  · dernière connexion {{ m.last_login_at ? formatDate(m.last_login_at) : 'jamais' }}
                </span>
              </div>
              <div class="flex gap-3">
                <button v-if="m.mfa && m.mfa.actif" class="text-amber-600 hover:underline" @click="debloquerMembre(m)">Débloquer la 2FA</button>
                <button class="text-red-600 hover:underline" @click="retirerMembre(m)">Retirer</button>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Événements récents -->
      <div>
        <h2 class="text-xl font-semibold text-gray-800 mb-4">Derniers événements</h2>
        <event-list :events="events" :show-device-info="true" :show-ident="true"
                    :more-link="{ path: '/admin/events', query: { clientId: $route.params.clientId } }" />
      </div>
    </div>

    <!-- Modal ajout dispositif -->
    <div v-if="showAddDeviceModal" class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="showAddDeviceModal = false"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Ajouter un dispositif UltraLINK</h3>
            <div v-if="addDeviceError" class="mb-4 p-2 bg-red-50 text-red-600 text-sm rounded">{{ addDeviceError }}</div>
            <div class="space-y-4">
              <div>
                <label class="form-label">IMEI (15 chiffres)</label>
                <input type="text" v-model="newDevice.ident" class="form-control"
                       placeholder="863456789012345" maxlength="15">
                <p class="text-xs text-gray-400 mt-1">L'IMEI du module SIM7080G, visible au boot.</p>
              </div>
              <div>
                <label class="form-label">Nom du dispositif</label>
                <input type="text" v-model="newDevice.name" class="form-control"
                       placeholder="Portail principal">
              </div>
              <div>
                <label class="form-label">Localisation</label>
                <input type="text" v-model="newDevice.location" class="form-control"
                       placeholder="30 rue de la Paix, Paris">
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
            <button @click="addDevice" class="btn btn-primary" :disabled="addDeviceLoading">
              <span v-if="addDeviceLoading" class="inline-block animate-spin mr-1">⟳</span>
              Créer
            </button>
            <button @click="showAddDeviceModal = false" class="btn btn-secondary">Annuler</button>
          </div>
        </div>
      </div>
    </div>
  </admin-layout>
</template>

<script>
import { enParis } from '@/i18n/dates';
import { defineComponent, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import AdminLayout from '@/components/layout/AdminLayout.vue';
import EventList from '@/components/common/EventList.vue';
import apiService from '@/services/api.service';

export default defineComponent({
  name: 'AdminClientDetails',
  components: { AdminLayout, EventList },

  setup() {
    const store  = useStore();
    const router = useRouter();
    const route  = useRoute();
    const clientId = parseInt(route.params.clientId);

    const loading = ref(true);
    const actionLoading = ref(false);
    const client = ref(null);
    const devices = ref([]);
    const events = ref([]);

    const editMode = ref(false);
    const editedClient = ref({});

    const showAddDeviceModal = ref(false);
    const addDeviceLoading = ref(false);
    const addDeviceError = ref('');
    const newDevice = ref({ ident: '', name: '', location: '' });

    // ---- Membres du compte (comptes partagés) ----
    const membres = ref([]);
    const invitationsMembres = ref([]);
    const avisMembres = ref(null);
    const chargerMembres = async () => {
      try {
        const { data } = await apiService.listerMembresClient(clientId);
        membres.value = data.membres || [];
        invitationsMembres.value = data.invitations || [];
      } catch (e) { console.error('[MEMBRES]', e); }
    };
    const signalerMembres = (ok, texte) => { avisMembres.value = { ok, texte }; setTimeout(() => { avisMembres.value = null; }, 6000); };
    const retirerMembre = async (m) => {
      if (!window.confirm(`Retirer l'accès de ${m.name} (${m.email}) à ce compte ? Sa session est coupée immédiatement.`)) return;
      try { const { data } = await apiService.retirerMembreClient(clientId, m.id); signalerMembres(true, data.message); await chargerMembres(); }
      catch (e) { signalerMembres(false, e.response?.data?.message || 'Retrait impossible.'); }
    };
    // Même geste que pour un admin ou un titulaire : identité à vérifier HORS LIGNE avant.
    const debloquerMembre = async (m) => {
      if (!window.confirm(`Effacer le second facteur de ${m.name} ? Vérifiez son identité hors ligne avant. Il le reconfigurera à sa prochaine connexion.`)) return;
      try { const { data } = await apiService.reinitialiserMfa('member', m.id); signalerMembres(true, data.message); await chargerMembres(); }
      catch (e) { signalerMembres(false, e.response?.data?.message || 'Déblocage impossible.'); }
    };

    onMounted(async () => {
      try {
        const result = await store.dispatch('admin/fetchClientDetails', clientId);
        client.value  = result.client;
        devices.value = result.devices || [];
        events.value  = result.events || [];
        await chargerMembres();
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    });

    const formatDate = (d) => {
      if (!d) return 'N/A';
      try { return format(enParis(parseISO(d)), "dd MMM yyyy HH:mm", { locale: fr }); }
      catch { return d; }
    };

    const getStateBadge = (device) => {
      if (!device.active) return 'bg-gray-100 text-gray-600';
      switch (device.state) {
        case 'armed':     return 'bg-orange-100 text-orange-700';
        case 'triggered': return 'bg-red-100 text-red-700';
        case 'disarmed':  return 'bg-green-100 text-green-700';
        default:          return 'bg-gray-100 text-gray-500';
      }
    };

    const getStateLabel = (device) => {
      if (!device.active) return 'Inactif';
      switch (device.state) {
        case 'armed':     return 'Armé';
        case 'triggered': return 'Déclenché';
        case 'disarmed':  return 'Désarmé';
        // Aucun message reçu depuis le rattachement : « Inconnu » laissait croire à une panne,
        // alors que la carte n'a simplement pas encore été allumée (ou pas encore parlé).
        default:          return device.last_seen ? 'Inconnu' : 'Jamais connectée';
      }
    };

    // Édition client
    const startEdit  = () => { editedClient.value = { ...client.value }; editMode.value = true; };
    const cancelEdit = () => { editMode.value = false; };

    const saveChanges = async () => {
      actionLoading.value = true;
      try {
        await store.dispatch('admin/updateClient', { clientId, clientData: editedClient.value });
        Object.assign(client.value, editedClient.value);
        editMode.value = false;
      } catch (e) {
        console.error(e);
      } finally {
        actionLoading.value = false;
      }
    };

    const toggleClientStatus = async (active) => {
      actionLoading.value = true;
      try {
        await store.dispatch('admin/toggleClientStatus', { clientId, active });
        client.value.active = active;
      } catch (e) {
        console.error(e);
      } finally {
        actionLoading.value = false;
      }
    };

    const dateParis = v => v ? new Date(v).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'long', timeStyle: 'short' }) : '—';
    const annulerSuppression = async () => {
      if (!confirm(`Annuler la suppression du compte "${client.value.name}" ? Le client en sera prévenu par courriel.`)) return;
      actionLoading.value = true;
      try {
        await apiService.annulerSuppressionClient(clientId);
        client.value.suppression = null;
      } catch (e) {
        alert(e.response?.data?.message || 'Annulation impossible.');
      } finally {
        actionLoading.value = false;
      }
    };

    const deleteClient = async () => {
      if (!confirm(`Supprimer le client "${client.value.name}" et tous ses dispositifs ?`)) return;
      actionLoading.value = true;
      try {
        await store.dispatch('admin/deleteClient', clientId);
        router.push('/admin/clients');
      } catch (e) {
        console.error(e);
      } finally {
        actionLoading.value = false;
      }
    };

    // Ajout dispositif
    const addDevice = async () => {
      if (!newDevice.value.ident || !newDevice.value.name || !newDevice.value.location) {
        addDeviceError.value = 'Tous les champs sont requis.';
        return;
      }
      addDeviceLoading.value = true;
      addDeviceError.value = '';
      try {
        const result = await store.dispatch('device/createDevice', {
          client_id: clientId,
          ...newDevice.value
        });
        devices.value.push({ id: result.deviceId, client_id: clientId, ...newDevice.value, active: true, state: 'unknown' });
        newDevice.value = { ident: '', name: '', location: '' };
        showAddDeviceModal.value = false;
      } catch (e) {
        addDeviceError.value = e.response?.data?.message || 'Erreur lors de la création';
      } finally {
        addDeviceLoading.value = false;
      }
    };

    return {
      loading, actionLoading, client, devices, events,
      editMode, editedClient, startEdit, cancelEdit, saveChanges,
      toggleClientStatus, deleteClient, dateParis, annulerSuppression,
      showAddDeviceModal, addDeviceLoading, addDeviceError, newDevice, addDevice,
      formatDate, getStateBadge, getStateLabel,
      membres, invitationsMembres, avisMembres, retirerMembre, debloquerMembre
    };
  }
});
</script>
